import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';
import { hasAdminPortalAccess } from '../services/permissions';
import { Loader2 } from 'lucide-react';

// Re-export NicknameSession type from dataService for components that import it here
export type { NicknameSession } from '../services/dataService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'teacher' | 'student' | 'board_admin';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { currentUser, userProfile, loading } = useAuth();
  const { schoolAssociations } = useSchool();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-white/70">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow anonymous Firebase users (nickname-only students) to access student portal
  if (requiredRole === 'student' && currentUser?.isAnonymous) {
    return <>{children}</>;
  }

  if (!currentUser || !userProfile) {
    return <Navigate to="/login" replace />;
  }

  const isAdminUser = hasAdminPortalAccess(userProfile.role, schoolAssociations);

  if (requiredRole === 'board_admin') {
    // Grant access to board admins and school-level admins (principal/vp/office_staff)
    if (!isAdminUser) {
      return userProfile.role === 'teacher'
        ? <Navigate to="/app" replace />
        : <Navigate to="/student" replace />;
    }
    return <>{children}</>;
  }

  if (requiredRole === 'teacher') {
    // Every non-student has a personal teacher workspace: /app lists only classes
    // you own, so reaching it is never an escalation. Board admins were previously
    // bounced back to /admin here, which left them permanently unable to open their
    // own gradebook — /admin is their home route, so there was no way out at all.
    // Principals/VPs/office staff are teachers too and have always passed through.
    if (userProfile.role !== 'teacher' && userProfile.role !== 'board_admin') {
      return <Navigate to="/student" replace />;
    }
    return <>{children}</>;
  }

  if (requiredRole === 'student') {
    if (userProfile.role !== 'student') {
      // A non-student may open /student to participate in classes they JOINED as
      // a participant (PD / staff-collaboration spaces) — the portal's loader
      // filters to classes where they have a roster record, so an owner never
      // sees their own gradebooks here.
      //
      // Admins used to be bounced to /admin here, which meant a principal, VP, or
      // board admin who joined a colleague's class could never open it: the
      // dashboard's "Classes you've joined" card links to /student, and this sent
      // them to /admin instead. Nothing routes an admin here by accident —
      // homeRouteFor() returns /app or /admin for them, never /student — so
      // arriving here is always a deliberate navigation.
      if (userProfile.role === 'teacher' || userProfile.role === 'board_admin' || isAdminUser) {
        return <>{children}</>;
      }
      return <Navigate to="/app" replace />;
    }
    return <>{children}</>;
  }

  return <>{children}</>;
};
